import type { AppContribution } from "@/composition";
import { createMemo, For, Show } from "solid-js";
import { Link, useLocation, useNavigate, useSearch } from "@tanstack/solid-router";
import { cn } from "@time-tracker/shared";
import {
  Bug,
  ChevronsUpDown,
  LogOut,
  Mail,
  Palette,
  Scan,
  Shield,
  ShieldAlert,
  Timer,
  Type,
  User,
  UserCircle,
  Users,
} from "@fleet/icons";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { Button } from "@/components/ui/button";
import { Separator } from "@fleet/ui";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { Avatar } from "@/components/ui/avatar";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { useUserPreferences } from "@/providers/user-preferences-context";
import { useKernel } from "@fleet/kernel";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

const getCount = (_key: string): number | undefined => undefined;

interface AppSidebarProps {
  stats: () =>
    | {
        all: number;
        myDay: number;
        important: number;
        planned: number;
        inbox: number;
      }
    | undefined;
  onCloseSidebar?: () => void;
}

const SMART_VIEWS = [
  { to: "/chronos", label: msg`Chronos Sync`, icon: Timer, key: "Chronos" },
] satisfies { to: string; label: MessageDescriptor; icon: unknown; key: string }[];

const ADMIN_ITEMS = [
  { to: "/organizations", label: msg`Organizations`, icon: Users },
  { to: "/invitations", label: msg`Invitations`, icon: Mail },
  { to: "/admin/storage", label: msg`Storage`, icon: Shield },
  { to: "/admin/audit-log", label: msg`Audit Log`, icon: Scan },
  { to: "/admin/themes", label: msg`Themes`, icon: Palette },
  { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
  { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
] satisfies { to: string; label: MessageDescriptor; icon: unknown }[];

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const { preferences } = useUserPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  const layoutSearch = useSearch({ strict: false });
  const { t } = useI18n();
  const { promptSignOut } = useSignOut();

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  // URL-addressable account switcher (#15b pattern): `?account=switch` opens the
  // single overlay mounted by `AccountSwitcherHost`. The trigger only flips the
  // flag on the current `/u/<n>` route.
  const openSwitcher = () => {
    props.onCloseSidebar?.();
    void navigate({
      to: ".",
      search: (prev) => ({ ...(prev as Record<string, unknown>), account: "switch" }),
      replace: true,
    });
  };

  // Reactive view of the active account for the trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch / sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    void layoutSearch();
    return readActiveAccountSync();
  });
  const activeLabel = () => {
    const a = activeAccount();
    return a?.kind === "real" ? a.displayName : t(msg`Guest`);
  };
  // The ACTIVE account's photo comes from the local row (reactive and offline);
  // the account registry is only the bootstrap value, because it hydrates from
  // `get-session` and is not refreshed on every write.
  const activeImage = () => preferences()?.image ?? null;

  const kernelApp = useKernel<AppContribution>();
  const contributedNav = createMemo(() =>
    kernelApp()
      .contributions.filter(
        (c): c is Extract<import("@/composition").AppContribution, { kind: "nav" }> =>
          c.kind === "nav",
      )
      .map((c) => c.item)
      .sort((a, b) => a.order - b.order),
  );

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Link
          to="/"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          onClick={() => props.onCloseSidebar?.()}
        >
          <Logo size="sm" />
        </Link>
      </div>

      <Separator />

      <nav class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide">
        <For each={SMART_VIEWS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              count={getCount(item.key || "")}
              exact={item.to === "/chronos" || item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>
        <For each={contributedNav()}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <Show when={session.data?.user.role === "admin"}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        {/* ── Account switcher (multi-account #18) ──────────────────────── */}
        <button
          type="button"
          onClick={openSwitcher}
          class={cn(
            "mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
            inactiveClass,
          )}
          data-testid="account-switcher-trigger"
        >
          <Show
            when={activeAccount()?.kind === "real" ? activeAccount() : null}
            fallback={
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <UserCircle class="h-4 w-4" />
              </span>
            }
          >
            {(account) => (
              <Avatar
                userId={account().userId}
                name={account().displayName}
                email={account().email}
                image={activeImage() ?? account().avatar}
                size="sm"
                testId="sidebar-avatar"
              />
            )}
          </Show>
          <span class="min-w-0 flex-1 truncate text-sm font-semibold">{activeLabel()}</span>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
        </button>

        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
